import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  TierProgramDetail,
  TierProgramMatrixRow,
  TierProgramRowKind,
} from "@colab/shared";

export type ReorderRowsInput = {
  programId: string;
  kind: TierProgramRowKind;
  orderedIds: string[];
};

type Context = {
  previousDetail: TierProgramDetail | undefined;
};

// PATCH /api/admin/tiers/programs/:programId/rows/order
//
// Optimistic — DnD reorder within a row_kind (requirement OR benefit). Rows
// of the other kind stay where they are; only the dragged kind's rows get
// repositioned. Mirrors use-reorder-tiers but scoped by kind.
export function useReorderTierRows() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ReorderRowsInput, Context>({
    mutationFn: ({ programId, kind, orderedIds }) =>
      apiFetch<{ ok: true }>(
        `/admin/tiers/programs/${programId}/rows/order`,
        {
          method: "PATCH",
          body: JSON.stringify({ kind, orderedIds }),
        },
      ),
    onMutate: async ({ programId, kind, orderedIds }) => {
      const key = queryKeys.tiers.program(programId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail = queryClient.getQueryData<TierProgramDetail>(key);
      queryClient.setQueryData<TierProgramDetail>(key, (old) => {
        if (!old) return old;
        const byId = new Map<string, TierProgramMatrixRow>(
          old.rows.map((r) => [r.id, r]),
        );
        if (orderedIds.some((id) => !byId.has(id))) return old;
        const nextOfKind: TierProgramMatrixRow[] = orderedIds.map((id, idx) => {
          const r = byId.get(id);
          if (!r) throw new Error("unreachable: row id checked above");
          return { ...r, position: idx };
        });
        const others = old.rows.filter((r) => r.row_kind !== kind);
        // Preserve the "row_kind asc then position asc" sort the server
        // returns by interleaving deterministically. The exact total order
        // gets refetched on settle.
        return { ...old, rows: [...nextOfKind, ...others] };
      });
      return { previousDetail };
    },
    onError: (err, { programId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.tiers.program(programId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to reorder rows");
    },
    onSettled: (_data, _err, { programId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tiers.program(programId),
      });
    },
  });
}
